import type { DialogProps } from '@radix-ui/react-dialog';

import ActionButton from '@/components/Button/ActionButton';
import Icon from '@/components/Icon/Icon';
import VStack from '@/components/Layout/VStack';
import { Text } from '@/components/Typography';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';

import { AdditionalAuthGate } from './AdditionalAuthGate';

type AdditionalAuthModalProps = DialogProps & {
  /**
   * モーダルが開かれた時に自動的にフローを初期化するかどうか
   * @default true
   */
  autoInit?: boolean;
  /**
   * 説明文の上書き（BE の displayMessage 等）。未指定時は既定の翻訳文言を表示する。
   */
  description?: string;
  /**
   * 追加認証成功後に実行するコールバック
   */
  onAuthSuccess?: () => Promise<void> | void;
};

/**
 * 追加認証モーダルコンポーネント
 *
 * MFA（多要素認証）による追加認証を行うモーダルです。
 * aal=aal2でログインフローを初期化し、MFA認証フォームを表示します。
 */
export function AdditionalAuthModal(props: AdditionalAuthModalProps) {
  const { open, autoInit = true, description, onAuthSuccess, ...rest } = props;
  const handleOpenChange = (value: boolean) => {
    props.onOpenChange?.(value);
  };
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.auth.additionalAuth',
  });

  const handleAuthSuccess = async () => {
    if (onAuthSuccess) {
      await onAuthSuccess();
    }
    // 成功時にモーダルを閉じる
    handleOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} {...rest}>
      <DialogContent className={cn('max-w-dialog-sm')}>
        <DialogHeader>
          <VStack className="items-center gap-3 mb-2">
            <Icon
              iconName="ShieldCheckIcon"
              className="text-primary-on-neutral-fill"
              size={48}
            />
            <DialogTitle>{t('title')}</DialogTitle>
          </VStack>
          <DialogDescription asChild>
            <Text variant="p" className="text-muted-foreground text-center">
              {description ?? t('description')}
            </Text>
          </DialogDescription>
        </DialogHeader>

        <VStack className="gap-5 py-3">
          <AdditionalAuthGate
            onAuthSuccess={handleAuthSuccess}
            autoInit={autoInit && open}
          />
        </VStack>

        <DialogFooter>
          <DialogClose asChild>
            <ActionButton variant="ghost">{t('cancel')}</ActionButton>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
